// virtual-office/public/net.js を TS 化して移植。
// 差分:
//   - audioCtx は外から渡される (audio-graph と共有)
//   - ピアの出力は `voiceBus` (= zoneFilter を通らないバス) に流す。声は
//     ゾーンに関係なく常にクリアに聞こえる (BAR では BGM だけこもる)
//   - 空間化は PannerNode(HRTF) ではなく StereoPannerNode + 手動距離 Gain。
//     pan は ±PAN_LIMIT に clamp して片耳完全消失を防ぐ (イヤホン視聴者に優しい)
//   - WebSocket エンドポイントは /club/ws 固定 (room パラメータは取らない)
//   - ConvolverNode 送りはしない (DJ 配信だけリバーブが掛かる)

export interface Self {
  id: string;
  x: number;
  y: number;
  color: string;
  name: string;
  /** 自分が聞き専モード (= ?listenOnly=1 で入室) なら true。welcome.self 経由で
   *  サーバが echo してくれる。voice mesh を一切作らず、label 末尾に 🔇 を付ける。 */
  listenOnly?: boolean;
}

export interface Peer {
  id: string;
  x: number;
  y: number;
  color: string;
  name: string;
  /** サーバが broadcast したアバター key (welcome.peers / peer-joined 由来)。
   *  入室時に各 peer が `?avatar=<key>` で渡した値で、DO 側 allowlist 検証済み。
   *  undefined のときは scene 側が peer.id ハッシュで決定論的にフォールバックする。 */
  avatarKey?: string;
  /** 聞き専モード。call (= SDP offer) を skip し、label 末尾に 🔇 を付ける。 */
  listenOnly?: boolean;
}

interface PeerRuntime extends Peer {
  pc?: RTCPeerConnection;
  /** リモート MediaStream の MediaStreamAudioSourceNode。明示 disconnect 用に保持 */
  source?: MediaStreamAudioSourceNode;
  /** マイク差個人差を均す per-peer compressor (source → activationGain → comp → distGain) */
  comp?: DynamicsCompressorNode;
  /** 半径外でフェード out / 半径内でフェード in する gate gain (clickless on/off) */
  activationGain?: GainNode;
  distGain?: GainNode;
  stereoPan?: StereoPannerNode;
  /** HRTF feature flag が ON の時に使う PannerNode (= panningModel: "HRTF")。
   *  /club3d Phase 3 の HRTF 再評価ゲート (= issue #122) で 1 度だけ実機計測
   *  するための switchable code path。デフォルトは未使用 (= 従来通り
   *  stereoPan を使う)。 */
  pannerHRTF?: PannerNode;
  audioEl?: HTMLAudioElement;
  analyser?: AnalyserNode;
  volBuf?: Uint8Array<ArrayBuffer>;
  /**
   * 半径判定の現在状態。半径内 (= voiceBus に接続中) なら true、
   * 半径外 (= source が disconnect 済み、CPU/帯域節約) なら false。
   * 初期値 false にしておき、attach 後に updatePeerActivation で実態に合わせる。
   */
  isActive?: boolean;
  /**
   * fade-out 完了で source.disconnect() を発火する setTimeout のハンドル。
   * 再度半径内に戻った場合はキャンセルして disconnect しない (= 再 connect 不要)。
   */
  // ReturnType<typeof setTimeout> で Node/Browser 両環境互換 (= future test code 用)。
  fadeOutTimer?: ReturnType<typeof setTimeout>;
  /**
   * remoteDescription が未設定の段階で届いた ICE candidate を溜めるキュー。
   * SDP を setRemoteDescription した直後に flush する。
   * これが無いと「ICE が SDP より先に届く」だけで peer connection が確立できない race がある。
   */
  pendingCandidates?: RTCIceCandidateInit[];
}

export interface NetHandlers {
  onSelf?: (self: Self) => void;
  onPeerJoin?: (peer: Peer) => void;
  onPeerMove?: (id: string, x: number, y: number) => void;
  onPeerLeave?: (id: string) => void;
  onPeerRename?: (id: string, name: string) => void;
  /** peer の聞き専モードが切り替わった時に呼ばれる (= サーバ broadcast 経由)。
   *  scene 側で label の 🔇 suffix を更新する用途。 */
  onPeerListenStateChange?: (id: string, listenOnly: boolean) => void;
  onClose?: () => void;
  onCrowdChange?: (count: number) => void;
  /**
   * ピア音声の <audio> play() が autoplay policy などで reject されたときに呼ばれる。
   * HUD は出さず console.warn で済ませる前提 (個別ピアの問題なので)。
   */
  onAudioError?: (peerId: string) => void;
}

const RTC_CONFIG: RTCConfiguration = {
  iceServers: [{ urls: "stun:stun.cloudflare.com:3478" }],
};

// ピア音声の空間化パラメータ。
//   PEER_REF_DIST 以下なら gain ≒ 1。それより遠いと inverse rolloff で減衰。
//   LATERAL_SCALE = 横方向に何 px ずれたら pan=±1 相当か。clamp で抑えるので
//   実際に到達するのは ±PAN_LIMIT。
const PEER_REF_DIST = 60;
const PEER_ROLLOFF = 1.2;
const PEER_LATERAL_SCALE = 400;
const PEER_PAN_LIMIT = 0.7;
const PARAM_TIME_CONST = 0.05;

// per-peer voice compressor。マイク個人差 (声デカい人/小さい人) を均すため軽く掛ける。
// threshold/ratio は会話用途の標準値。attack を 5ms にして子音は通す。
const PEER_COMP_THRESHOLD_DB = -24;
const PEER_COMP_RATIO = 4;
const PEER_COMP_ATTACK = 0.005;
const PEER_COMP_RELEASE = 0.1;

// dynamic disconnect (= D4): 半径 R 以内のピア音声だけ Web Audio graph に流す。
//   - VOICE_ACTIVE_RADIUS / VOICE_INACTIVE_RADIUS の差 (= 100px) がヒステリシス。
//     scene.ts 側で peer 描画に 100ms の interpolation delay が入っているので、
//     「視覚上で peer が境界線を跨ぐ瞬間」と「voice on/off」のズレを吸収するために
//     60px → 100px に広げる (game programmer review medium)。
//   - VOICE_FADE_TIME_CONST は setTargetAtTime の time constant (秒)。τ=60ms で
//     1τ=63%、3τ=95% 到達。耳の click 検知閾値以下に収まる (audio DSP medium)。
//   - VOICE_FADE_OUT_MS は fade-out 完了後に source.disconnect() するまでの猶予。
//     τ × 約 6 (= 残量 0.2%) で disconnect 時の段差を可聴範囲外にする。
const VOICE_ACTIVE_RADIUS = 600;
const VOICE_INACTIVE_RADIUS = 700;
const VOICE_FADE_TIME_CONST = 0.06;
const VOICE_FADE_OUT_MS = 350;

export class Net {
  readonly h: NetHandlers;
  me: Self | null = null;
  readonly peers = new Map<string, PeerRuntime>();
  private audioCtx: AudioContext;
  private peerDestination: AudioNode;
  private localStream: MediaStream;
  private localSource: MediaStreamAudioSourceNode | null = null;
  private localAnalyser: AnalyserNode | null = null;
  private localVolBuf: Uint8Array<ArrayBuffer> | null = null;
  private ws: WebSocket | null = null;
  /** WebSocket 接続先パス。`/club` (2D) からは `/club/ws` がデフォルト、
   *  `/club3d` からは `/club3d/ws` を渡す。`Net.start()` が `location.href` を
   *  base に組み立てる。 */
  private readonly wsPath: string;
  /** HRTF feature flag (issue #122)。default false。true で attachPeer 時に
   *  StereoPanner の代わりに PannerNode (panningModel: "HRTF") を使う。 */
  private readonly hrtf: boolean;
  private requestedName: string;
  /** 入室時に WS URL `?avatar=<key>` で渡すアバター key。null なら付けない。 */
  private requestedAvatarKey: string | null;
  /** 聞き専モード。入室時に true なら voice mesh を一切作らず、call も受けない。
   *  upgradeToTalking() で false に遷移する (= 途中で talking 参加)。 */
  listenOnly: boolean;
  muted = false;
  /**
   * scene.ts が update() で毎フレーム書き込む自分の最新座標 (滑らかに動いた直後の値)。
   * sendMove と違いネットワーク送信は伴わず、active 判定にのみ使う (= dynamic disconnect 用)。
   * 初期値は NaN にして「まだ来ていない」を識別する。
   */
  private playerX = Number.NaN;
  private playerY = Number.NaN;

  /**
   * @param audioCtx audio-graph 側で resume 済みの AudioContext
   * @param voiceBus ピア StereoPanner の合流先 (通常 graph.voiceBus)。
   *   zoneFilter / wetSend を通らない経路にすることで、BAR でも声はクリア。
   * @param localStream getUserMedia 済みのマイク stream。聞き専モードでは空の
   *   `new MediaStream()` を渡してよい。
   * @param requestedName 入室時に WebSocket open 直後に name 送信する
   * @param requestedAvatarKey 入室時に WS URL `?avatar=<key>` で渡すアバター key。
   *   null なら付けない (= DO 側で undefined になり、他クライアントは hash fallback)。
   * @param listenOnly true で入室すると voice mesh を一切作らない。
   *   `upgradeToTalking()` で talking モードに昇格できる。
   */
  constructor(
    audioCtx: AudioContext,
    voiceBus: AudioNode,
    localStream: MediaStream,
    requestedName: string,
    requestedAvatarKey: string | null,
    listenOnly: boolean,
    handlers: NetHandlers,
    opts: { wsPath?: string; hrtf?: boolean } = {},
  ) {
    this.audioCtx = audioCtx;
    this.peerDestination = voiceBus;
    this.localStream = localStream;
    this.requestedName = requestedName;
    this.requestedAvatarKey = requestedAvatarKey;
    this.listenOnly = listenOnly;
    this.h = handlers;
    // `/club` (= 2D Phaser) は既定の `/club/ws`、`/club3d` は `/club3d/ws` を
    // 明示的に渡す。default を維持しているので /club 側の既存呼び出しは破壊しない。
    this.wsPath = opts.wsPath ?? "/club/ws";
    // HRTF feature flag (issue #122)。/club3d Phase 3 の再評価ゲートで 1 度だけ
    // 有効化して実機計測する。default off で /club 側の挙動は完全不変。
    this.hrtf = opts.hrtf ?? false;
  }

  async start(): Promise<void> {
    // 聞き専で入った時は makeAnalyser を呼ばない (= 空の MediaStream に
    // createMediaStreamSource を渡すとブラウザによっては InvalidState になる)。
    // upgradeToTalking() で初めて analyser を作る。
    if (!this.listenOnly) {
      this.localAnalyser = this.makeAnalyser(this.localStream);
    }

    // Page Visibility: タブが隠れた瞬間に全 voice peer を即 inactive 化して
    // graph + decode CPU を落とす。bg では setTimeout が throttle されて
    // 通常の fade-out 経路が 1-2s 後ろにずれるので、可視性 hidden は同期で扱う。
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          this.deactivateAllForBackground();
        }
        // visible 復帰時は scene.update() の次 tick で updatePlayerPos が呼ばれ、
        // 半径内 peer が自動 re-activate される (= 何もしなくていい)。
      });
    }

    const wsUrl = new URL(this.wsPath, location.href);
    wsUrl.protocol = wsUrl.protocol.replace("http", "ws");
    // アバター選択を URL クエリで渡す (= DO 側 allowlist で検証される)。
    // 入室時の 1 回だけ送って attachment に焼き付ければ、その後の `welcome.peers` /
    // `peer-joined` broadcast に乗って他クライアントが正しいスプライトを描画できる。
    if (this.requestedAvatarKey) {
      wsUrl.searchParams.set("avatar", this.requestedAvatarKey);
    }
    // 聞き専モードも URL クエリで渡す (= サーバ attachment に焼き付けて他 peer に
    // broadcast)。途中で talking 昇格した時は `listen-state` メッセージで上書き。
    if (this.listenOnly) {
      wsUrl.searchParams.set("listenOnly", "1");
    }
    this.ws = new WebSocket(wsUrl.toString());

    await new Promise<void>((resolve, reject) => {
      const ws = this.ws!;
      const onOpen = () => {
        ws.removeEventListener("error", onError);
        resolve();
      };
      const onError = () => {
        ws.removeEventListener("open", onOpen);
        reject(new Error("WebSocket open failed (auth or network)"));
      };
      ws.addEventListener("open", onOpen, { once: true });
      ws.addEventListener("error", onError, { once: true });
    });

    this.ws.addEventListener("message", (e) => void this.onMessage(e));
    this.ws.addEventListener("close", () => this.h.onClose?.());

    if (this.requestedName) {
      this.send({ type: "name", name: this.requestedName });
    }
  }

  sendMove(x: number, y: number): void {
    if (!this.me) return;
    this.me.x = x;
    this.me.y = y;
    this.send({ type: "move", x: Math.round(x), y: Math.round(y) });
    for (const id of this.peers.keys()) this.updateSpatial(id);
  }

  /**
   * scene.ts 側から **毎フレーム** 呼ばれる前提の self position hook。
   * sendMove と分ける理由: sendMove は throttling されて ~5Hz で動くが、active 判定の
   * ヒステリシス境界では即時反応したいため。
   *
   * high-frequency call に耐える設計: 値が前回と同じならピアループは早期 return。
   * 同じ x,y でも稀に呼ばれる程度なら epsilon を見ずに === 比較で十分。
   */
  updatePlayerPos(x: number, y: number): void {
    if (x === this.playerX && y === this.playerY) return;
    this.playerX = x;
    this.playerY = y;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    for (const id of this.peers.keys()) this.updatePeerActivation(id);
  }

  setMicMuted(muted: boolean): void {
    this.muted = muted;
    for (const track of this.localStream.getAudioTracks()) {
      track.enabled = !muted;
    }
  }

  /** 聞き専で入った人が後からマイクで voice mesh に参加するための一方向遷移。
   *  - 既に talking ならノーオプ
   *  - 新しい localStream に差し替え、analyser を作る (= 既存 source が残ってれば release)
   *  - 既存 peer のうち talking 状態の人にだけ SDP offer を**並列で**打って mesh に乗る
   *    一部 peer の SDP が失敗しても他は影響を受けない (= Gemini high)
   *  - サーバに `listen-state` を送る → 他 peer に peer-listen-state が broadcast
   *    されて label の 🔇 が消える */
  async upgradeToTalking(newStream: MediaStream): Promise<void> {
    if (!this.listenOnly) return;
    // defensive: 旧 source が残ってたら release (= 将来 downgrade → upgrade を
    // サポートした時の destination 残り線を予防、audio DSP review medium)
    if (this.localSource) {
      try { this.localSource.disconnect(); } catch { /* ignore */ }
      this.localSource = null;
    }
    this.localStream = newStream;
    this.listenOnly = false;
    this.muted = false;
    if (this.me) this.me.listenOnly = false;
    // analyser を初めて作る (= 入室時は skip していた)
    this.localAnalyser = this.makeAnalyser(this.localStream);
    // talking 状態の既存 peer 全員に **並列で** call (= 直列 await だと N 人の ICE
    // を順番に待って upgrade 完了まで秒単位かかる、game programmer review medium)。
    // 個別 peer の SDP 失敗は他 peer に伝播させない (Gemini high)。
    const targets = [...this.peers.values()].filter((p) => !p.listenOnly);
    const results = await Promise.allSettled(targets.map((p) => this.callPeer(p.id)));
    for (let i = 0; i < results.length; i++) {
      const r = results[i];
      if (r && r.status === "rejected") {
        console.warn("[club/net] upgradeToTalking: callPeer failed for", targets[i]?.id, r.reason);
      }
    }
    // サーバに通知 → 他 peer に peer-listen-state broadcast (= 🔇 消える)
    this.send({ type: "listen-state", listenOnly: false });
  }

  getLocalVolume(): number {
    if (this.muted) return 0;
    if (!this.localAnalyser) return 0;
    if (!this.localVolBuf || this.localVolBuf.length !== this.localAnalyser.fftSize) {
      this.localVolBuf = new Uint8Array(this.localAnalyser.fftSize);
    }
    return readRms(this.localAnalyser, this.localVolBuf);
  }

  getPeerVolume(peerId: string): number {
    const peer = this.peers.get(peerId);
    if (!peer || !peer.analyser) return 0;
    if (!peer.volBuf || peer.volBuf.length !== peer.analyser.fftSize) {
      peer.volBuf = new Uint8Array(peer.analyser.fftSize);
    }
    return readRms(peer.analyser, peer.volBuf);
  }

  close(): void {
    for (const peer of this.peers.values()) {
      this.releasePeerAudio(peer);
    }
    this.peers.clear();
    try { this.ws?.close(); } catch { /* ignore */ }
    this.ws = null;
    // 自分のマイクのトラックを止めて、ブラウザの録音インジケータ + デバイス握りを解放
    for (const track of this.localStream.getTracks()) {
      try { track.stop(); } catch { /* ignore */ }
    }
    if (this.localSource) {
      try { this.localSource.disconnect(); } catch { /* ignore */ }
      this.localSource = null;
    }
    if (this.localAnalyser) {
      try { this.localAnalyser.disconnect(); } catch { /* ignore */ }
      this.localAnalyser = null;
    }
  }

  // ピアの音声ノードを destination から切り離して GC 可能にする。
  // ピアが抜けた時 / Net.close() 時 / 同じピアに再 attach する時に呼ぶ。
  // 注意: <audio> は srcObject = null を明示しないと MediaStream の参照が残り、
  // ブラウザがリソースを開放しない (= 古い peer の getUserMedia track が掴まれっぱなし)
  private releasePeerAudio(peer: PeerRuntime): void {
    try { peer.pc?.close(); } catch { /* ignore */ }
    if (peer.audioEl) {
      try { peer.audioEl.pause(); } catch { /* ignore */ }
      try { peer.audioEl.srcObject = null; } catch { /* ignore */ }
      try { peer.audioEl.remove(); } catch { /* ignore */ }
    }
    if (peer.fadeOutTimer !== undefined) {
      clearTimeout(peer.fadeOutTimer);
      peer.fadeOutTimer = undefined;
    }
    try { peer.source?.disconnect(); } catch { /* ignore */ }
    try { peer.activationGain?.disconnect(); } catch { /* ignore */ }
    try { peer.comp?.disconnect(); } catch { /* ignore */ }
    try { peer.distGain?.disconnect(); } catch { /* ignore */ }
    try { peer.stereoPan?.disconnect(); } catch { /* ignore */ }
    try { peer.pannerHRTF?.disconnect(); } catch { /* ignore */ }
    try { peer.analyser?.disconnect(); } catch { /* ignore */ }
    peer.pc = undefined;
    peer.audioEl = undefined;
    peer.source = undefined;
    peer.activationGain = undefined;
    peer.comp = undefined;
    peer.distGain = undefined;
    peer.stereoPan = undefined;
    peer.pannerHRTF = undefined;
    peer.analyser = undefined;
    peer.isActive = false;
  }

  // ─────────────────────────────────────

  private makeAnalyser(stream: MediaStream): AnalyserNode {
    const source = this.audioCtx.createMediaStreamSource(stream);
    this.localSource = source;
    const analyser = this.audioCtx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.6;
    source.connect(analyser);
    // 出力には繋がない (= 計測専用)
    return analyser;
  }

  private send(obj: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj));
    }
  }

  private async onMessage(evt: MessageEvent): Promise<void> {
    let msg: { type?: string; [k: string]: unknown };
    try {
      msg = JSON.parse(typeof evt.data === "string" ? evt.data : "");
    } catch {
      return;
    }

    if (msg.type === "welcome") {
      const self = msg.self as Self;
      this.me = { ...self };
      this.h.onSelf?.(this.me);
      const peers = (msg.peers as Peer[]) ?? [];
      for (const peer of peers) {
        this.peers.set(peer.id, { ...peer });
        this.h.onPeerJoin?.(peer);
        // 自分が聞き専 or 相手が聞き専なら SDP offer を打たない (= voice mesh 未構築)。
        // 自分が後で talking 昇格した時は upgradeToTalking() が改めて callPeer する。
        if (!this.listenOnly && !peer.listenOnly) {
          await this.callPeer(peer.id);
        }
      }
      this.h.onCrowdChange?.(this.peers.size);
      return;
    }

    if (msg.type === "peer-joined") {
      const peer = msg.peer as Peer;
      this.peers.set(peer.id, { ...peer });
      this.h.onPeerJoin?.(peer);
      this.h.onCrowdChange?.(this.peers.size);
      return;
    }

    if (msg.type === "peer-listen-state") {
      const id = String(msg.id);
      const listenOnly = msg.listenOnly === true;
      const p = this.peers.get(id);
      if (p) p.listenOnly = listenOnly;
      this.h.onPeerListenStateChange?.(id, listenOnly);
      // 同時 upgrade race の対策 (Gemini high): 自分が talking で、相手がいま
      // talking に昇格 (= listenOnly: true → false)、かつ二者間に PeerConnection
      // が無い (= upgrader 側の upgradeToTalking が「peers Map で相手を listenOnly
      // と認識した瞬間に call スキップ」した) ケースを補う。
      //
      // WebRTC glare 対策: 両者が同時に call すると both have-local-offer になって
      // setRemoteDescription が state machine 違反でこける。`my id > their id` の
      // 一方向ルールで「id が大きい方だけ call する」のように決定論的に分岐させる。
      // - 普通のケース (= A talking、B が listenOnly → talking 昇格): B が
      //   upgradeToTalking で必ず A を call する。A 側のこの handler が呼ばれる時には
      //   既に p.pc が居る → skip。
      // - レアケース (= 両方 listenOnly → 同時昇格): どちらも upgradeToTalking で
      //   相手を skip する。peer-listen-state 受信後、id 大きい側だけ call。
      if (
        !listenOnly &&
        !this.listenOnly &&
        p &&
        !p.pc &&
        this.me &&
        this.me.id > id
      ) {
        void this.callPeer(id).catch((e) =>
          console.warn("[club/net] callPeer after peer-listen-state failed", id, e),
        );
      }
      return;
    }

    if (msg.type === "peer-left") {
      const id = String(msg.id);
      const p = this.peers.get(id);
      if (p) {
        this.releasePeerAudio(p);
        this.peers.delete(id);
      }
      this.h.onPeerLeave?.(id);
      this.h.onCrowdChange?.(this.peers.size);
      return;
    }

    if (msg.type === "peer-moved") {
      const id = String(msg.id);
      const x = Number(msg.x);
      const y = Number(msg.y);
      const p = this.peers.get(id);
      if (p && Number.isFinite(x) && Number.isFinite(y)) {
        p.x = x;
        p.y = y;
        this.updateSpatial(id);
        // 相手が動いた = 半径境界をまたぐ可能性。active 判定し直す。
        this.updatePeerActivation(id);
        this.h.onPeerMove?.(id, x, y);
      }
      return;
    }

    if (msg.type === "peer-renamed") {
      const id = String(msg.id);
      const name = String(msg.name);
      const p = this.peers.get(id);
      if (p) p.name = name;
      this.h.onPeerRename?.(id, name);
      return;
    }

    if (msg.type === "signal") {
      await this.onSignal(String(msg.from), msg.data as SignalData);
      return;
    }
  }

  private async callPeer(peerId: string): Promise<void> {
    const pc = this.ensurePc(peerId);
    if (!pc) return;
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    // **trickle ICE**: ICE gathering 完了を待たずに SDP を即送信する。
    // この時点で pc.localDescription.sdp に candidate はほぼ含まれていないが、
    // ensurePc で登録した icecandidate イベントハンドラが個別に candidate を
    // 相手に push する。リモート側は onSignal で pendingCandidates にキューして
    // setRemoteDescription 後に flush するので race は起きない。
    // (Cloudflare Stream WHEP も trickle ICE 対応済み。live.ts が
    // waitForIceGathering で gathering 完了を待っているのは歴史的事情で、
    // 将来 trickle に統一する余地がある — 今回は scope 外)
    this.send({ type: "signal", to: peerId, data: { sdp: pc.localDescription } });
  }

  private ensurePc(peerId: string): RTCPeerConnection | null {
    const peer = this.peers.get(peerId);
    if (!peer) return null;
    if (peer.pc) return peer.pc;

    const pc = new RTCPeerConnection(RTC_CONFIG);
    peer.pc = pc;

    for (const track of this.localStream.getTracks()) {
      pc.addTrack(track, this.localStream);
    }

    pc.addEventListener("icecandidate", (e) => {
      if (e.candidate) {
        this.send({ type: "signal", to: peerId, data: { ice: e.candidate.toJSON() } });
      }
    });

    pc.addEventListener("track", (e) => {
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      this.attachRemoteStream(peerId, stream);
    });

    return pc;
  }

  private async onSignal(from: string, data: SignalData): Promise<void> {
    const pc = this.ensurePc(from);
    if (!pc) return;
    const peer = this.peers.get(from);

    // SDP negotiation はコーデック非互換 / 状態ミスマッチ / 経路変動などで reject し得る。
    // unhandled rejection でシグナリングフロー全体を落とさないよう try/catch で包む。
    if (data.sdp) {
      try {
        await pc.setRemoteDescription(data.sdp);
        if (data.sdp.type === "offer") {
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          this.send({ type: "signal", to: from, data: { sdp: pc.localDescription } });
        }
        // SDP 適用後、それまでに溜まっていた pending ICE candidate を flush
        if (peer && peer.pendingCandidates && peer.pendingCandidates.length > 0) {
          for (const cand of peer.pendingCandidates) {
            try { await pc.addIceCandidate(cand); }
            catch (err) { console.warn("[club/net] pending ICE failed", err); }
          }
          peer.pendingCandidates = [];
        }
      } catch (err) {
        console.warn("[club/net] SDP signaling failed", err);
      }
      return;
    }
    if (data.ice && peer) {
      // remoteDescription がまだ無い段階で来た candidate を addIceCandidate すると
      // InvalidStateError になるブラウザがある。pending キューに溜めて後で flush する。
      if (pc.remoteDescription) {
        try {
          await pc.addIceCandidate(data.ice);
        } catch (err) {
          console.warn("[club/net] addIceCandidate failed", err);
        }
      } else {
        if (!peer.pendingCandidates) peer.pendingCandidates = [];
        peer.pendingCandidates.push(data.ice);
      }
    }
  }

  private attachRemoteStream(peerId: string, stream: MediaStream): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;

    // 同一ピアで attachRemoteStream が再度呼ばれた場合 (例: WebRTC renegotiation や
    // duplicate track イベント) は古いノードを完全に切り離してから新規生成する。
    // これをやらないと古い <audio> と Web Audio ノードが destination に残り続けてリーク。
    if (
      peer.audioEl || peer.source || peer.activationGain || peer.comp || peer.distGain ||
      peer.stereoPan || peer.pannerHRTF || peer.analyser
    ) {
      if (peer.audioEl) {
        try { peer.audioEl.pause(); } catch { /* ignore */ }
        try { peer.audioEl.srcObject = null; } catch { /* ignore */ }
        try { peer.audioEl.remove(); } catch { /* ignore */ }
      }
      if (peer.fadeOutTimer !== undefined) {
        clearTimeout(peer.fadeOutTimer);
        peer.fadeOutTimer = undefined;
      }
      try { peer.source?.disconnect(); } catch { /* ignore */ }
      try { peer.activationGain?.disconnect(); } catch { /* ignore */ }
      try { peer.comp?.disconnect(); } catch { /* ignore */ }
      try { peer.distGain?.disconnect(); } catch { /* ignore */ }
      try { peer.stereoPan?.disconnect(); } catch { /* ignore */ }
      try { peer.pannerHRTF?.disconnect(); } catch { /* ignore */ }
      try { peer.analyser?.disconnect(); } catch { /* ignore */ }
    }
    peer.isActive = false;

    // ブラウザ実装の都合: <audio> にも srcObject を渡して mute 再生
    const audioEl = new Audio();
    audioEl.srcObject = stream;
    audioEl.muted = true;
    audioEl.autoplay = true;
    void audioEl.play().catch((err) => {
      // autoplay policy / 省電力モード等で失敗するケース。サイレントに切らず
      // console + 上位コールバックに通知してデバッグを楽にする。
      console.warn(`[club/net] peer ${peerId} audio play failed`, err);
      this.h.onAudioError?.(peerId);
    });
    peer.audioEl = audioEl;

    const source = this.audioCtx.createMediaStreamSource(stream);
    peer.source = source;

    // 半径外フェードアウト用 gate gain。初期値 0 で attach 直後はミュート、
    // updatePeerActivation で半径内なら 1 にフェードイン、半径外なら 0 のまま
    // → fade-out 完了後 source.disconnect() で CPU 節約。
    const activationGain = this.audioCtx.createGain();
    activationGain.gain.value = 0;
    peer.activationGain = activationGain;

    // マイク個人差を均す per-peer compressor。声デカい人で耳が痛くならない / 声小さい人が
    // 沈まないようにするのが目的。閾値 -24dB、ratio 4 で会話用途の標準値。
    const comp = this.audioCtx.createDynamicsCompressor();
    comp.threshold.value = PEER_COMP_THRESHOLD_DB;
    comp.knee.value = 6;
    comp.ratio.value = PEER_COMP_RATIO;
    comp.attack.value = PEER_COMP_ATTACK;
    comp.release.value = PEER_COMP_RELEASE;
    peer.comp = comp;

    // 距離による gain (inverse rolloff)。updateSpatial で値が更新される
    const distGain = this.audioCtx.createGain();
    distGain.gain.value = 0;

    // 左右パン (clamp 済み)。HRTF と違って完全片耳消失にならない。
    // HRTF feature flag (issue #122) が ON の場合は代わりに PannerNode を作って
    // 3D position 駆動にする (= /club3d Phase 3 の再評価ゲート用)。
    let stereoPan: StereoPannerNode | undefined;
    let pannerHRTF: PannerNode | undefined;
    let panSink: AudioNode;
    if (this.hrtf) {
      pannerHRTF = this.audioCtx.createPanner();
      pannerHRTF.panningModel = "HRTF";
      // distance attenuation は引き続き distGain で手動制御するので、
      // PannerNode 側の距離モデルは無効化 (= refDistance 巨大、rolloffFactor 0)。
      pannerHRTF.distanceModel = "inverse";
      pannerHRTF.refDistance = 10000;
      pannerHRTF.rolloffFactor = 0;
      panSink = pannerHRTF;
    } else {
      stereoPan = this.audioCtx.createStereoPanner();
      stereoPan.pan.value = 0;
      panSink = stereoPan;
    }

    // 配線: (source) → activationGain → comp → distGain → panSink → voiceBus
    // activationGain は initial 0 のため、updatePeerActivation が半径内と判定するまで無音。
    // 半径外と判定された場合は fade-out 完了後に source.disconnect() で完全切断する。
    source.connect(activationGain);
    activationGain.connect(comp);
    comp.connect(distGain);
    distGain.connect(panSink);
    panSink.connect(this.peerDestination);

    peer.distGain = distGain;
    peer.stereoPan = stereoPan;
    peer.pannerHRTF = pannerHRTF;

    // 発話インジケータ用 (出力には繋がない)。compressor の **前** から取って
    // 「実際にどれだけマイクが鳴っているか」を反映する。activationGain の **前** = source
    // から分岐するので、半径外で source.disconnect() しても analyser だけは生き続ける
    // (= 半径外でも「喋っているかどうか」が UI 側で見える)。
    const analyser = this.audioCtx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.6;
    source.connect(analyser);
    peer.analyser = analyser;

    this.updateSpatial(peerId);
    this.updatePeerActivation(peerId);
  }

  private updateSpatial(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer || !peer.distGain || !this.me) return;
    const dx = peer.x - this.me.x;
    const dy = peer.y - this.me.y;
    const d = Math.hypot(dx, dy);
    const t = this.audioCtx.currentTime;

    if (peer.pannerHRTF) {
      // HRTF mode: peer の **3D world 座標** を panner に書く (= listener 側
      // で audioCtx.listener position/forward を camera と連動)。voice mesh は
      // /club / /club3d で 2D 投影 x,y を message contract に持つので、3D 化は
      // PannerNode の position に直接 x,0,z を書く形にする。
      peer.pannerHRTF.positionX.setTargetAtTime(peer.x, t, PARAM_TIME_CONST);
      peer.pannerHRTF.positionY.setTargetAtTime(0, t, PARAM_TIME_CONST);
      peer.pannerHRTF.positionZ.setTargetAtTime(peer.y, t, PARAM_TIME_CONST);
    } else if (peer.stereoPan) {
      const pan = clamp(dx / PEER_LATERAL_SCALE, -PEER_PAN_LIMIT, PEER_PAN_LIMIT);
      peer.stereoPan.pan.setTargetAtTime(pan, t, PARAM_TIME_CONST);
    }

    const gain = PEER_REF_DIST / (PEER_REF_DIST + d * PEER_ROLLOFF);
    peer.distGain.gain.setTargetAtTime(gain, t, PARAM_TIME_CONST);
  }

  /** HRTF mode 用: AudioContext.listener の位置と向きを self 座標 + 向きで
   *  毎フレーム更新する。/club3d scene tick から呼ばれる前提。issue #122。
   *  hrtf=false でも no-op、誤呼び出しは害なし。 */
  updateListenerOrientation(
    selfX: number,
    selfY: number,
    forwardX: number,
    forwardZ: number,
  ): void {
    if (!this.hrtf) return;
    const t = this.audioCtx.currentTime;
    const l = this.audioCtx.listener;
    if (l.positionX) {
      l.positionX.setTargetAtTime(selfX, t, PARAM_TIME_CONST);
      l.positionY.setTargetAtTime(0, t, PARAM_TIME_CONST);
      l.positionZ.setTargetAtTime(selfY, t, PARAM_TIME_CONST);
      l.forwardX.setTargetAtTime(forwardX, t, PARAM_TIME_CONST);
      l.forwardY.setTargetAtTime(0, t, PARAM_TIME_CONST);
      l.forwardZ.setTargetAtTime(forwardZ, t, PARAM_TIME_CONST);
      l.upX.setTargetAtTime(0, t, PARAM_TIME_CONST);
      l.upY.setTargetAtTime(1, t, PARAM_TIME_CONST);
      l.upZ.setTargetAtTime(0, t, PARAM_TIME_CONST);
    } else {
      // 古い browser 用 fallback (positionX 等の AudioParam が無い)。
      // setPosition / setOrientation は deprecated だが Safari など互換のため残す。
      // (DeprecationWarning が console に出るのは想定内)
      l.setPosition(selfX, 0, selfY);
      l.setOrientation(forwardX, 0, forwardZ, 0, 1, 0);
    }
  }

  /**
   * dynamic disconnect: 半径 R 以内のピアだけ voice graph に流す。
   *
   * - 半径内 (= d <= VOICE_ACTIVE_RADIUS) → source を活性化、activationGain を 0→1 で
   *   fade in。pending fade-out timer があればキャンセル。source が disconnect 済みなら
   *   reconnect する (= reattach はしない、source ノードはそのまま再利用)。
   * - 半径外 (= d > VOICE_INACTIVE_RADIUS) → activationGain を 1→0 で fade out、
   *   VOICE_FADE_OUT_MS 後に source.disconnect() で完全切断 (CPU/帯域節約)。
   * - hysteresis band 内 (= ACTIVE < d <= INACTIVE) → 現状の active 状態を維持。
   *
   * peer.source は MediaStream を握り続けるので、disconnect しても再 connect 時に
   * 同じ source ノードを使い回せる。<audio> の play() も止めない (= MediaStream の
   * 「生きてる」感を保つ + 再 active 化時の audible glitch を避ける)。
   */
  private updatePeerActivation(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer || !peer.source || !peer.activationGain) return;
    if (!Number.isFinite(this.playerX) || !Number.isFinite(this.playerY)) return;

    const dx = peer.x - this.playerX;
    const dy = peer.y - this.playerY;
    const d = Math.hypot(dx, dy);

    const wasActive = peer.isActive === true;
    let shouldActivate: boolean;
    if (wasActive) {
      // 一度 active な peer は INACTIVE 半径を超えるまで active 維持 (= hysteresis)
      shouldActivate = d <= VOICE_INACTIVE_RADIUS;
    } else {
      // inactive な peer は ACTIVE 半径以内に入って初めて activate
      shouldActivate = d <= VOICE_ACTIVE_RADIUS;
    }

    if (shouldActivate === wasActive) return; // 状態変化なし → 何もしない

    const t = this.audioCtx.currentTime;
    if (shouldActivate) {
      // inactive → active: pending disconnect をキャンセル、必要なら source を再 connect、
      // gain を 0 → 1 へ fade in
      if (peer.fadeOutTimer !== undefined) {
        clearTimeout(peer.fadeOutTimer);
        peer.fadeOutTimer = undefined;
      }
      try { peer.source.connect(peer.activationGain); } catch { /* already connected */ }
      // distGain を「あるべき値」(= 現在距離由来の inverse rolloff) に **pre-warm**
      // してから activationGain の fade-in を発火する。これをやらないと distGain と
      // activationGain の τ が同時に立ち上がって S 字カーブになり、聴感で
      // 「最初しばらく聞こえない → 急に立ち上がる」となる (audio DSP review)。
      this.updateSpatial(peerId);
      peer.activationGain.gain.cancelScheduledValues(t);
      peer.activationGain.gain.setTargetAtTime(1, t, VOICE_FADE_TIME_CONST);
      // dummy <audio> も再開: muted=true でも一部ブラウザは pause 時に decode を
      // sleep するので、active な peer だけ decode コストを払う (browser API medium)。
      peer.audioEl?.play().catch(() => undefined);
      peer.isActive = true;
    } else {
      // active → inactive: gain を 1 → 0 へ fade out。fade 完了後に source.disconnect()。
      peer.activationGain.gain.cancelScheduledValues(t);
      peer.activationGain.gain.setTargetAtTime(0, t, VOICE_FADE_TIME_CONST);
      peer.isActive = false;
      if (peer.fadeOutTimer !== undefined) clearTimeout(peer.fadeOutTimer);
      peer.fadeOutTimer = setTimeout(() => {
        peer.fadeOutTimer = undefined;
        // タイマー発火までの間に再度 active 化された / peer がいなくなった可能性がある
        if (peer.isActive) return;
        try { peer.source?.disconnect(peer.activationGain!); } catch { /* already gone */ }
        // 半径外の peer は dummy <audio> も pause して decode コストを落とす
        try { peer.audioEl?.pause(); } catch { /* ignore */ }
      }, VOICE_FADE_OUT_MS);
    }
  }

  /** SRE review medium: __clubDebug.netInfo に「実効果」を出すための getter。
   *  isActive=true の peer 数 (= 実際に Web Audio graph に繋がっている数) を返す。 */
  getActivePeerCount(): number {
    let count = 0;
    for (const peer of this.peers.values()) {
      if (peer.isActive === true) count++;
    }
    return count;
  }

  /** Page Visibility API: タブが隠れている間は全 peer を即 inactive 化して
   *  decode + graph CPU を落とす (SRE + Browser API review medium)。
   *  visible 復帰時に scene の update() loop が再開すれば、距離判定で
   *  自動 reconcile される (= 何もしなくていい)。 */
  deactivateAllForBackground(): void {
    const t = this.audioCtx.currentTime;
    for (const peer of this.peers.values()) {
      if (!peer.isActive || !peer.activationGain) continue;
      peer.activationGain.gain.cancelScheduledValues(t);
      peer.activationGain.gain.setTargetAtTime(0, t, VOICE_FADE_TIME_CONST);
      peer.isActive = false;
      if (peer.fadeOutTimer !== undefined) {
        clearTimeout(peer.fadeOutTimer);
        peer.fadeOutTimer = undefined;
      }
      // bg では setTimeout が throttle されて disconnect が遅れる ──
      // 同期で source.disconnect しても fade は AudioContext.currentTime 駆動なので問題なし
      try { peer.source?.disconnect(peer.activationGain); } catch { /* ignore */ }
      try { peer.audioEl?.pause(); } catch { /* ignore */ }
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

interface SignalData {
  sdp?: RTCSessionDescriptionInit;
  ice?: RTCIceCandidateInit;
}

function readRms(analyser: AnalyserNode, buf: Uint8Array<ArrayBuffer>): number {
  analyser.getByteTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = (buf[i]! - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / buf.length);
}
